import { clientToViewBox, unitScale, type ViewBox } from './zoom-viewport';
import { createDoubleTapTracker } from './double-tap';
import { createSvgEl } from './svg';

export interface CartesianDomain {
  xDomain: [number, number];
  yDomain: [number, number];
}

export interface CartesianPlotRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CartesianViewportView extends CartesianDomain {
  zoom: number;
}

export interface CartesianViewportOptions {
  base: CartesianDomain;
  plotRect: CartesianPlotRect;
  minZoom?: number;
  maxZoom?: number;
  onChange(view: CartesianViewportView): void;
  isPanTarget?(target: Element): boolean;
  /**
   * Plot-owned SVG root or selector. Descendant paint targets receive
   * `touch-action: none` without changing unrelated content elsewhere in an
   * inset SVG. Required when `plotRect` does not cover the full viewBox.
   */
  touchActionTarget?: SVGElement | string;
}

export interface CartesianViewportHandle {
  getView(): CartesianViewportView;
  setView(view: CartesianDomain): void;
  setBase(base: CartesianDomain, options?: { notify?: boolean }): void;
  reset(): void;
  dispose(): void;
}

const WHEEL_SENSITIVITY = 0.0015;
const PAINTED_SVG_TARGETS =
  'circle, ellipse, foreignObject, image, line, path, polygon, polyline, rect, text, tspan, use';
const clamp = (value: number, lower: number, upper: number): number =>
  Math.min(upper, Math.max(lower, value));

interface TouchActionSnapshot {
  value: string;
  priority: string;
}

function plotRectCoversViewBox(plotRect: CartesianPlotRect, viewBox: ViewBox): boolean {
  const epsilon = 1e-9;
  return (
    Math.abs(plotRect.x - viewBox.x) <= epsilon &&
    Math.abs(plotRect.y - viewBox.y) <= epsilon &&
    Math.abs(plotRect.width - viewBox.width) <= epsilon &&
    Math.abs(plotRect.height - viewBox.height) <= epsilon
  );
}

/** Map a normalized coordinate in [0, 1] to a domain value. */
export function interpolateDomain(domain: [number, number], fraction: number): number {
  return domain[0] + (domain[1] - domain[0]) * fraction;
}

/**
 * Scale a Cartesian domain about a mathematical anchor. The x span controls
 * zoom; y is rebuilt from the base aspect so both axes always zoom uniformly.
 */
export function zoomCartesianDomainAbout(
  view: CartesianDomain,
  anchor: { x: number; y: number },
  factor: number,
  base: CartesianDomain,
  limits: { minZoom: number; maxZoom: number },
): CartesianDomain {
  const baseWidth = base.xDomain[1] - base.xDomain[0];
  const baseHeight = base.yDomain[1] - base.yDomain[0];
  const viewWidth = view.xDomain[1] - view.xDomain[0];
  const zoomNow = baseWidth / viewWidth;
  const zoomNext = clamp(zoomNow * factor, limits.minZoom, limits.maxZoom);
  const scale = zoomNow / zoomNext;
  const width = viewWidth * scale;
  const height = baseHeight / zoomNext;
  const xFraction = (anchor.x - view.xDomain[0]) / viewWidth;
  const yFraction = (anchor.y - view.yDomain[0]) / (view.yDomain[1] - view.yDomain[0]);
  return {
    xDomain: [anchor.x - width * xFraction, anchor.x + width * (1 - xFraction)],
    yDomain: [anchor.y - height * yFraction, anchor.y + height * (1 - yFraction)],
  };
}

function cloneDomain(domain: CartesianDomain): CartesianDomain {
  return { xDomain: [...domain.xDomain], yDomain: [...domain.yDomain] as [number, number] };
}

function viewBoxFromSvg(svg: SVGSVGElement): ViewBox {
  const values = (svg.getAttribute('viewBox') ?? '')
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (values.length !== 4 || values.some((value) => !Number.isFinite(value))) {
    throw new Error('createCartesianViewport requires an SVG viewBox.');
  }
  const [x, y, width, height] = values;
  return { x: x!, y: y!, width: width!, height: height! };
}

export function createCartesianViewport(
  svg: SVGSVGElement,
  opts: CartesianViewportOptions,
): CartesianViewportHandle {
  const minZoom = opts.minZoom ?? 0.25;
  const maxZoom = opts.maxZoom ?? 8;
  const limits = { minZoom, maxZoom };
  const fixedViewBox = viewBoxFromSvg(svg);
  const touchActionTarget =
    opts.touchActionTarget ?? (plotRectCoversViewBox(opts.plotRect, fixedViewBox) ? svg : null);
  if (!touchActionTarget) {
    throw new Error(
      'createCartesianViewport requires touchActionTarget when plotRect does not cover the SVG viewBox.',
    );
  }
  let base = cloneDomain(opts.base);
  let view = cloneDomain(base);
  const pointers = new Map<number, { x: number; y: number }>();
  const doubleTap = createDoubleTapTracker();
  const touchActionTargets = new Map<SVGElement, TouchActionSnapshot>();
  let disposed = false;
  const surface = createSvgEl('rect', {
    x: opts.plotRect.x,
    y: opts.plotRect.y,
    width: opts.plotRect.width,
    height: opts.plotRect.height,
    fill: 'transparent',
    'pointer-events': 'all',
    'aria-hidden': 'true',
    'data-cartesian-viewport-surface': '',
  });
  surface.style.setProperty('touch-action', 'none', 'important');
  svg.insertBefore(surface, svg.firstChild);

  function restoreTouchAction(element: SVGElement, snapshot: TouchActionSnapshot): void {
    if (snapshot.value) {
      element.style.setProperty('touch-action', snapshot.value, snapshot.priority);
    } else {
      element.style.removeProperty('touch-action');
    }
  }

  function resolveTouchActionRoots(): SVGElement[] {
    if (typeof touchActionTarget === 'string') {
      return Array.from(svg.querySelectorAll<SVGElement>(touchActionTarget));
    }
    if (!(touchActionTarget instanceof SVGElement)) return [];
    return touchActionTarget === svg || svg.contains(touchActionTarget) ? [touchActionTarget] : [];
  }

  function topLevelSvgChild(element: SVGElement): SVGElement | null {
    let current = element;
    while (current.parentNode && current.parentNode !== svg) {
      if (!(current.parentNode instanceof SVGElement)) return null;
      current = current.parentNode;
    }
    return current.parentNode === svg ? current : null;
  }

  function placeSurfaceBehindPlotContent(roots: readonly SVGElement[]): void {
    const children = Array.from(svg.children);
    let firstPlotChild: SVGElement | null = null;
    let firstPlotIndex = Number.POSITIVE_INFINITY;
    for (const root of roots) {
      if (root === svg) continue;
      const child = topLevelSvgChild(root);
      if (!child) continue;
      const index = children.indexOf(child);
      if (index >= 0 && index < firstPlotIndex) {
        firstPlotChild = child;
        firstPlotIndex = index;
      }
    }
    if (firstPlotChild && surface.nextElementSibling !== firstPlotChild) {
      svg.insertBefore(surface, firstPlotChild);
    }
  }

  function refreshTouchActionTargets(): void {
    if (disposed) return;
    const roots = resolveTouchActionRoots();
    placeSurfaceBehindPlotContent(roots);
    const active = new Set<SVGElement>();
    for (const root of roots) {
      if (root !== svg) active.add(root);
      if (root.matches(PAINTED_SVG_TARGETS)) active.add(root);
      for (const element of root.querySelectorAll<SVGElement>(PAINTED_SVG_TARGETS)) {
        if (
          element === surface ||
          element.closest('defs, clipPath, mask, marker, pattern, symbol') !== null
        ) {
          continue;
        }
        active.add(element);
      }
    }

    for (const element of active) {
      if (!touchActionTargets.has(element)) {
        touchActionTargets.set(element, {
          value: element.style.getPropertyValue('touch-action'),
          priority: element.style.getPropertyPriority('touch-action'),
        });
      }
      if (
        element.style.getPropertyValue('touch-action') !== 'none' ||
        element.style.getPropertyPriority('touch-action') !== 'important'
      ) {
        element.style.setProperty('touch-action', 'none', 'important');
      }
    }

    for (const [element, snapshot] of touchActionTargets) {
      if (active.has(element) && svg.contains(element)) continue;
      restoreTouchAction(element, snapshot);
      touchActionTargets.delete(element);
    }
  }

  const touchTargetObserver = new MutationObserver((records) => {
    if (records.some((record) => record.type === 'childList' || record.attributeName !== 'style')) {
      refreshTouchActionTargets();
    }
  });
  touchTargetObserver.observe(svg, { attributes: true, childList: true, subtree: true });

  function isPanTarget(target: Element): boolean {
    return opts.isPanTarget ? opts.isPanTarget(target) : target.closest('[data-no-pan]') === null;
  }

  function currentView(): CartesianViewportView {
    return {
      ...cloneDomain(view),
      zoom: (base.xDomain[1] - base.xDomain[0]) / (view.xDomain[1] - view.xDomain[0]),
    };
  }

  function apply(next: CartesianDomain, notify = true): void {
    view = cloneDomain(next);
    if (notify) opts.onChange(currentView());
  }

  function toViewBoxPoint(clientX: number, clientY: number): { x: number; y: number } | null {
    const rect = svg.getBoundingClientRect();
    if (!(rect.width > 0 && rect.height > 0)) return null;
    return clientToViewBox(rect, fixedViewBox, clientX, clientY);
  }

  function toPlotPoint(clientX: number, clientY: number): { x: number; y: number } | null {
    const point = toViewBoxPoint(clientX, clientY);
    if (!point) return null;
    const right = opts.plotRect.x + opts.plotRect.width;
    const bottom = opts.plotRect.y + opts.plotRect.height;
    if (
      point.x < opts.plotRect.x ||
      point.x > right ||
      point.y < opts.plotRect.y ||
      point.y > bottom
    ) {
      return null;
    }
    return point;
  }

  function pointToDomain(point: { x: number; y: number }): { x: number; y: number } {
    const xFraction = (point.x - opts.plotRect.x) / opts.plotRect.width;
    const yFraction = (point.y - opts.plotRect.y) / opts.plotRect.height;
    return {
      x: interpolateDomain(view.xDomain, xFraction),
      y: interpolateDomain(view.yDomain, 1 - yFraction),
    };
  }

  function toDomain(clientX: number, clientY: number): { x: number; y: number } | null {
    const point = toPlotPoint(clientX, clientY);
    return point ? pointToDomain(point) : null;
  }

  function toCapturedDomain(clientX: number, clientY: number): { x: number; y: number } | null {
    const point = toViewBoxPoint(clientX, clientY);
    return point ? pointToDomain(point) : null;
  }

  function setView(next: CartesianDomain): void {
    doubleTap.cancel();
    const baseWidth = base.xDomain[1] - base.xDomain[0];
    const baseHeight = base.yDomain[1] - base.yDomain[0];
    const requestedWidth = next.xDomain[1] - next.xDomain[0];
    const zoom = clamp(baseWidth / requestedWidth, minZoom, maxZoom);
    const width = baseWidth / zoom;
    const height = baseHeight / zoom;
    const xCentre = (next.xDomain[0] + next.xDomain[1]) / 2;
    const yCentre = (next.yDomain[0] + next.yDomain[1]) / 2;
    apply({
      xDomain: [xCentre - width / 2, xCentre + width / 2],
      yDomain: [yCentre - height / 2, yCentre + height / 2],
    });
  }

  function reset(): void {
    doubleTap.cancel();
    apply(base);
  }

  function onWheel(event: WheelEvent): void {
    const anchor = toDomain(event.clientX, event.clientY);
    if (!anchor) return;
    event.preventDefault();
    doubleTap.cancel();
    const delta =
      event.deltaMode === 1
        ? event.deltaY * 16
        : event.deltaMode === 2
          ? event.deltaY * 100
          : event.deltaY;
    apply(
      zoomCartesianDomainAbout(view, anchor, Math.exp(-delta * WHEEL_SENSITIVITY), base, limits),
    );
  }

  function onPointerDown(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!(event.target instanceof Element) || !isPanTarget(event.target)) return;
    if (!toPlotPoint(event.clientX, event.clientY)) return;
    const active = document.activeElement;
    if (active instanceof HTMLElement && !svg.contains(active)) active.blur();
    if (event.pointerType === 'touch') {
      doubleTap.begin(event.pointerId, { x: event.clientX, y: event.clientY });
    }
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    try {
      svg.setPointerCapture(event.pointerId);
    } catch {
      // jsdom does not implement pointer capture.
    }
    svg.dataset.panning = 'true';
    event.preventDefault();
  }

  function panByClientDelta(dx: number, dy: number, domain: CartesianDomain): CartesianDomain {
    const rect = svg.getBoundingClientRect();
    const stageScale = unitScale(rect, fixedViewBox);
    const stageDx = dx / stageScale;
    const stageDy = dy / stageScale;
    const xShift = (stageDx / opts.plotRect.width) * (domain.xDomain[1] - domain.xDomain[0]);
    const yShift = (stageDy / opts.plotRect.height) * (domain.yDomain[1] - domain.yDomain[0]);
    return {
      xDomain: [domain.xDomain[0] - xShift, domain.xDomain[1] - xShift],
      yDomain: [domain.yDomain[0] + yShift, domain.yDomain[1] + yShift],
    };
  }

  function onPointerMove(event: PointerEvent): void {
    const previous = pointers.get(event.pointerId);
    if (!previous) return;
    if (event.pointerType === 'touch') {
      doubleTap.move(event.pointerId, { x: event.clientX, y: event.clientY });
    }
    const rect = svg.getBoundingClientRect();
    if (!(rect.width > 0 && rect.height > 0)) return;
    if (pointers.size === 1) {
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      apply(panByClientDelta(event.clientX - previous.x, event.clientY - previous.y, view));
      return;
    }

    const [idA, idB] = [...pointers.keys()];
    if (idA === undefined || idB === undefined) return;
    const a0 = pointers.get(idA)!;
    const b0 = pointers.get(idB)!;
    const moved = { x: event.clientX, y: event.clientY };
    const a1 = idA === event.pointerId ? moved : a0;
    const b1 = idB === event.pointerId ? moved : b0;
    const distance0 = Math.hypot(b0.x - a0.x, b0.y - a0.y) || 1;
    const distance1 = Math.hypot(b1.x - a1.x, b1.y - a1.y) || 1;
    const midpoint0 = { x: (a0.x + b0.x) / 2, y: (a0.y + b0.y) / 2 };
    const midpoint1 = { x: (a1.x + b1.x) / 2, y: (a1.y + b1.y) / 2 };
    const anchor = toCapturedDomain(midpoint0.x, midpoint0.y);
    if (!anchor) return;
    const zoomed = zoomCartesianDomainAbout(view, anchor, distance1 / distance0, base, limits);
    pointers.set(event.pointerId, moved);
    apply(panByClientDelta(midpoint1.x - midpoint0.x, midpoint1.y - midpoint0.y, zoomed));
  }

  function endPointer(event: PointerEvent, canceled: boolean): void {
    const shouldReset =
      event.pointerType === 'touch' &&
      doubleTap.end(event.pointerId, { x: event.clientX, y: event.clientY }, canceled);
    if (!pointers.delete(event.pointerId)) return;
    try {
      svg.releasePointerCapture(event.pointerId);
    } catch {
      // jsdom does not implement pointer capture.
    }
    if (pointers.size === 0) delete svg.dataset.panning;
    if (shouldReset) reset();
  }

  function onPointerUp(event: PointerEvent): void {
    endPointer(event, false);
  }

  function onPointerCancel(event: PointerEvent): void {
    endPointer(event, true);
  }

  function onDoubleClick(event: MouseEvent): void {
    if (!(event.target instanceof Element) || !isPanTarget(event.target)) return;
    if (!toPlotPoint(event.clientX, event.clientY)) return;
    event.preventDefault();
    reset();
  }

  svg.addEventListener('wheel', onWheel, { passive: false });
  svg.addEventListener('pointerdown', onPointerDown);
  svg.addEventListener('pointermove', onPointerMove);
  svg.addEventListener('pointerup', onPointerUp);
  svg.addEventListener('pointercancel', onPointerCancel);
  svg.addEventListener('dblclick', onDoubleClick);
  apply(base);
  refreshTouchActionTargets();

  return {
    getView: currentView,
    setView,
    setBase(nextBase, options) {
      doubleTap.cancel();
      base = cloneDomain(nextBase);
      view = cloneDomain(base);
      if (options?.notify !== false) opts.onChange(currentView());
    },
    reset,
    dispose() {
      if (disposed) return;
      disposed = true;
      touchTargetObserver.disconnect();
      svg.removeEventListener('wheel', onWheel);
      svg.removeEventListener('pointerdown', onPointerDown);
      svg.removeEventListener('pointermove', onPointerMove);
      svg.removeEventListener('pointerup', onPointerUp);
      svg.removeEventListener('pointercancel', onPointerCancel);
      svg.removeEventListener('dblclick', onDoubleClick);
      surface.remove();
      for (const [element, snapshot] of touchActionTargets) {
        restoreTouchAction(element, snapshot);
      }
      touchActionTargets.clear();
      delete svg.dataset.panning;
      pointers.clear();
      doubleTap.cancel();
    },
  };
}
